import { useMemo, useState } from "react";

import {
	Button,
	Chip,
	Listbox,
	ListboxItem,
	Spacer,
	Tooltip,
	User,
	cn,
	useDisclosure,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { ClientOnly, useLocation, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useBoolean, useMediaQuery } from "usehooks-ts";

import { LanguageSelect, Logo, ThemeSwitcher } from "@onesolution/components";
import {
	LS_LANGUAGE_KEY,
	SIDEBAR_ROUTES,
	type SidebarRouteType,
} from "@onesolution/constants";
import { useClientInfos, useLanguage, useUserInfos } from "@onesolution/hooks";
import { getTasksCountFromServer, logout } from "@onesolution/serverFn";

import { isUserRoleInAllowedRoles } from "@onesolution/utils";
import posthog from "posthog-js";
import NotificationsButton from "../NotificationsButton";
import SidebarDrawer from "./SidebarDrawer";

export default function Sidebar() {
	const { t, lang } = useLanguage();
	const userData = useUserInfos();
	const location = useLocation();
	const clientInfos = useClientInfos();

	const navigate = useNavigate();
	const logoutAction = useServerFn(logout);
	const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
	const { isOpen, onOpenChange } = useDisclosure();
	const { value: isCollapsed, toggle } = useBoolean(false);
	const isMobile = useMediaQuery("(max-width: 768px)");
	const { data: tasks } = getTasksCountFromServer.useQuery();
	const [openRoute, setOpenRoute] = useState<string>("");
	const disabledKeys = useMemo<string[]>(
		() =>
			SIDEBAR_ROUTES.flatMap((i) => i.children || [])
				.filter((item) => item.isDisabled)
				.map((item) => item.path),
		[],
	);
	const availablesRoutes = useMemo(() => {
		const availables = SIDEBAR_ROUTES.filter((item) =>
			isUserRoleInAllowedRoles(userData.roles, item.roles),
		) as SidebarRouteType[];

		return availables
			.concat(
				(clientInfos?.customNavigation.map((nav) => ({
					iconActive: nav.iconName,
					icon: nav.iconName,
					key: `custom.${nav.name}`,
					name: nav.name,
					path: nav.url,
					isChild: false,
					isDisabled: false,
					roles: [],
					custom: true,
				})) || []) as SidebarRouteType[],
			)
			.flatMap((item) => {
				if (!item.children?.length) return item;

				if (
					(
						[item.path, ...item.children.map((c) => c.path)] as string[]
					).includes(location.pathname) ||
					openRoute === item.name
				)
					return [item, ...item.children.map((c) => ({ ...c, isChild: true }))];

				return item;
			})
			.filter((r) => !clientInfos?.disabledFeatures.includes(r.key));
	}, [
		userData,
		clientInfos,
		location.pathname,
		openRoute,
		clientInfos?.customNavigation,
	]);

	return (
		<SidebarDrawer
			isCollapsed={isCollapsed}
			isOpen={isOpen}
			onOpenChange={onOpenChange}
		>
			<div
				className={cn(
					"will-change relative flex h-full w-72 flex-col p-6 transition-width backdrop-blur-xl bg-default-100/50 shadow-inner overflow-auto",
					{
						"w-20 items-center px-2 py-6": isCollapsed,
					},
				)}
			>
				<div
					className={cn("flex items-center gap-3 pl-2", {
						"justify-center gap-0 pl-0": isCollapsed,
					})}
				>
					<Logo />
					<div className={cn("flex-end flex", { hidden: isCollapsed })}>
						<Icon
							className="cursor-pointer [&>g]:stroke-[1px]"
							icon="solar:round-alt-arrow-left-line-duotone"
							width={24}
							height={24}
							onClick={isMobile ? onOpenChange : toggle}
						/>
					</div>
				</div>

				<Spacer y={12} />

				<div
					className={cn(
						"w-full flex items-center justify-between gap-2 -mr-4",
						{
							"-mr-0 justify-center gap-0": isCollapsed,
						},
					)}
				>
					<User
						name={userData.name}
						avatarProps={{ src: userData.avatar }}
						description={
							<Tooltip
								className="text-xs"
								placement="bottom"
								delay={500}
								content={userData.email}
							>
								<span>{userData.email}</span>
							</Tooltip>
						}
						classNames={{
							wrapper: cn({
								hidden: isCollapsed,
							}),
							name: cn("text-sm truncate  max-w-36", {
								hidden: isCollapsed,
							}),
							description: cn("text-xs max-w-36 truncate", {
								hidden: isCollapsed,
							}),
						}}
					/>
					<NotificationsButton />
				</div>

				<Spacer y={6} />

				<Listbox
					hideSelectedIcon
					aria-label="Sidebar Navigation"
					as="nav"
					key={`${isCollapsed ? "compact" : "default"}-${lang}-${tasks?.length}-${openRoute}`}
					className="list-none"
					classNames={{
						list: "items-center gap-2",
					}}
					color="primary"
					itemClasses={{
						base: "px-4 min-h-12 rounded-large h-12 data-[selected=true]:bg-primary/20",
						title:
							"text-sm font-medium text-default-500 group-data-[selected=true]:text-foreground",
					}}
					disabledKeys={disabledKeys}
					items={availablesRoutes}
					selectedKeys={[location.pathname]}
					selectionMode="single"
					variant="flat"
				>
					{(item: SidebarRouteType) => {
						return (
							<ListboxItem
								key={item.path}
								className={item.isChild && !isCollapsed ? "pl-12" : ""}
								aria-label={`Navigate to ${t(item.name!)}`}
								href={item.path as unknown as undefined}
								textValue={t(item.name!)}
								onClick={
									item.children?.length
										? (e) => {
												setOpenRoute(openRoute === item.name ? "" : item.name);
												e.stopPropagation();
												e.preventDefault();
											}
										: undefined
								}
								title={
									isCollapsed ? null : item.custom ? item.name : t(item.name!)
								}
								startContent={
									isCollapsed ? null : (
										<Icon
											className="text-default-500 group-data-[selected=true]:text-foreground"
											icon={item.icon!}
											width={24}
											height={24}
										/>
									)
								}
								endContent={
									<ClientOnly>
										{isCollapsed ? null : item.isDisabled ? (
											<Chip
												color="default"
												variant="bordered"
												radius="sm"
												className="absolute right-2 text-xs"
											>
												{t("basic.soon")}
											</Chip>
										) : item.name === "name.tasks" && tasks?.length ? (
											<Chip
												color="primary"
												variant="solid"
												radius="full"
												className="absolute right-2 text-xs w-8 h-8"
											>
												{tasks?.length}
											</Chip>
										) : null}
										{item.children?.length! > 0 &&
											!(
												[
													item.path,
													...(item.children?.map((c) => c.path) || []),
												] as string[]
											).includes(location.pathname) && (
												<Icon
													className="hover:bg-primary-300 w-6 h-6 p-1 rounded-lg transition-all"
													onClick={(e) => {
														setOpenRoute(
															openRoute === item.name ? "" : item.name,
														);
														e.stopPropagation();
														e.preventDefault();
													}}
													icon={
														openRoute !== item.name
															? "solar:alt-arrow-down-line-duotone"
															: "solar:alt-arrow-up-line-duotone"
													}
												/>
											)}
									</ClientOnly>
								}
							>
								<Tooltip
									content={item.custom ? item.name : t(item.name!)}
									placement="right"
								>
									<div className="flex w-full items-center justify-center">
										<Icon
											className="text-default-500 group-data-[selected=true]:text-foreground"
											icon={item.icon!}
											width={24}
											height={24}
										/>
									</div>
								</Tooltip>
							</ListboxItem>
						);
					}}
				</Listbox>

				<Spacer y={8} />

				<div
					className={cn("mt-auto flex flex-col gap-1", {
						"items-center": isCollapsed,
					})}
				>
					{isCollapsed && (
						<Button
							isIconOnly
							className="flex size-10 text-default-600"
							size="sm"
							variant="light"
							onPress={toggle}
						>
							<Icon
								className="cursor-pointer [&>g]:stroke-[1px]"
								icon="solar:round-alt-arrow-right-line-duotone"
								width={24}
								height={24}
							/>
						</Button>
					)}

					<div className={isCollapsed ? "" : "ml-1"}>
						<ThemeSwitcher size="lg" disableTooltip={!isCollapsed} />
					</div>

					<LanguageSelect collapsed={isCollapsed} />

					<Tooltip
						content={t("login.logout")}
						isDisabled={!isCollapsed}
						placement="right"
					>
						<Button
							className={cn(
								"justify-start text-default-500 data-[hover=true]:text-foreground data-[hover=true]:bg-danger",
								{
									"justify-center": isCollapsed,
								},
							)}
							isIconOnly={isCollapsed}
							startContent={
								isCollapsed ? null : (
									<Icon
										className="flex-none rotate-180 text-default-500"
										icon="solar:minus-circle-line-duotone"
										width={24}
										height={24}
									/>
								)
							}
							variant="light"
							onPress={async () => {
								const lang = localStorage.getItem(LS_LANGUAGE_KEY);
								localStorage.clear();
								localStorage.setItem(LS_LANGUAGE_KEY, lang!);
								posthog.reset();
								setIsLoggingOut(true);
								try {
									await logoutAction();
								} catch (e) {
									setIsLoggingOut(false);
									navigate((e as { options: { href: string } }).options);
								}
							}}
							isLoading={isLoggingOut}
						>
							{isCollapsed ? (
								<Icon
									className="rotate-180 text-default-500"
									icon="solar:minus-circle-line-duotone"
									width={24}
									height={24}
								/>
							) : (
								t("login.logout")
							)}
						</Button>
					</Tooltip>
				</div>
			</div>
		</SidebarDrawer>
	);
}
